// src/steps/Step7AssembleJourney.js
import React from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";

import TeachingPlanCard from "../components/TeachingPlanCard/TeachingPlanCard";
import JourneyDownloadCard from "../components/JourneyDownloadCard/JourneyDownloadCard";

/**
 * Main Step 7 component
 */
export default function Step7AssembleJourney({
  finalMd,
  loading,
  error,
  onRetry,
  journeySummary,
  onDownload,
  plan,
  onPlanChange,
  aiFooter,
}) {
  const canDownload = !!finalMd && !loading && !error;
  const hasRows = (plan?.rows || []).length > 0;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Step 7 · Assemble Journey
      </Typography>

      {/* Plain instructional text (similar pattern to Step 6) */}
      <Typography variant="body2" color="text.primary" sx={{ mb: 2 }}>
        This step generates a <strong>session-by-session teaching plan</strong>{" "}
        based on the curated MLUs and your notes. The result is a
        Markdown table with sessions, selected MLUs, teaching methods, and an
        example course structure. You can edit the plan below; your changes are
        saved automatically and used in the download.
      </Typography>

      {/* Minimal disclaimer, styled like the LLM processing / Step 6 card */}
      <Box sx={{ mt: 2, mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: "background.default",
            borderLeft: "4px solid",
            borderColor: "grey.600",
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
          }}
        >
          <VerifiedUserOutlinedIcon
            sx={{
              fontSize: 24,
              color: "grey.700",
              mt: 0.2,
            }}
          />
          <Typography variant="body2" color="text.secondary">
            To create this plan, the K8 LJD Agent uses the{" "}
            <strong>learning outcomes of all 72 MLUs</strong>, the{" "}
            <strong>learning philosophy of the T4R project</strong>, and the{" "}
            <strong>framework conditions you described</strong> for the learning
            situation and the learner&apos;s needs.
          </Typography>
        </Paper>
      </Box>

      {/* Loading state */}
      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Generating teaching plan…</Typography>
        </Box>
      )}

      {/* Error state with retry */}
      {error && !loading && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            onRetry && (
              <Button color="inherit" size="small" onClick={onRetry}>
                Retry
              </Button>
            )
          }
        >
          {error}
        </Alert>
      )}

      {/* Result: journey map with the teaching plan (preview, or editable when expanded), download */}
      {!loading && finalMd && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 3 }}>
            Teaching plan (preview)
          </Typography>
          <TeachingPlanCard finalMd={finalMd} plan={plan} onPlanChange={hasRows ? onPlanChange : null} disabled={loading} />

          {aiFooter}

          <JourneyDownloadCard
            finalMd={finalMd}
            journeySummary={journeySummary}
            disabled={!canDownload}
            onDownload={onDownload}
          />
        </>
      )}

      {/* No result yet, no error, no loading */}
      {!loading && !error && !finalMd && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Preparing the teaching plan…
        </Typography>
      )}
    </Box>
  );
}
